"use client";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Typography from "../../atoms/typography";
import { SortDesktop } from "../sorts";
import PLPReviewRatings from "../../molecules/plpReviewRatings";
import SkeletonList from "../../molecules/skeletons/skeletonList";
import { LongDescription } from "../../../containers/productListingPage/longDescription";

/**
 * PlpTitleRowDesktop component for desktop view of Product Listing Page.
 * @param {Object} props - Component props.
 * @param {Array} props.sortData - Sorting options data.
 * @param {number} props.totalProducts - Total number of products.
 * @param {number} props.bestRating - Best rating.
 * @param {string} props.productCategoryName - Product category name.
 * @param {number} props.productList - Number of products listed.
 * @returns {JSX.Element} - PlpTitleRowDesktop component.
 */
const PlpTitleRowDesktop = ({
  isMobile,
  sortData,
  totalProducts,
  bestRating,
  productCategoryName,
  productList,
  reviewCount,
  onScrollToReviews,
  categoryId,
  isLoaded = true,
  plpATF = "",
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const shouldShowSkeleton = isMounted && !isLoaded;

  return (
    <>
      <div className="mb-12 flex justify-between">
        <div className="mx-2 flex items-center justify-start gap-16 lg:w-[72%] xll:w-[75%]">
          {shouldShowSkeleton ? (
            <SkeletonList
              count={1}
              className="mx-2"
              itemClassName=""
              itemStyle={{ width: "650px", height: "27px", borderRadius: "8px" }}
            />
          ) : (
            <>
              <Typography
                classes="mx-2 overflow-hidden text-ellipsis whitespace-nowrap text-24 max-w-[64%] font-600"
                Tag={"h1"}
                variant="h2"
                itemProp="name"
              >
                {productCategoryName}
              </Typography>
              <div className="mx-2 text-16 text-grey-500">
                {productList} of {totalProducts} Gifts
              </div>
              {reviewCount > 0 && (
                <>
                  <div className="mx-2 text-16 font-400 text-grey-500">|</div>
                  <PLPReviewRatings
                    isMobile={isMobile}
                    bestRating={bestRating}
                    reviewCount={reviewCount}
                    onScrollToReviews={onScrollToReviews}
                  />
                </>
              )}
            </>
          )}
        </div>
        <div className="mx-2 flex w-[24%] items-center justify-end">
          {shouldShowSkeleton ? (
            <SkeletonList
              count={1}
              className=""
              itemClassName=""
              itemStyle={{ width: "198px", height: "41px", borderRadius: "8px" }}
            />
          ) : (
            <SortDesktop data={sortData} categoryId={categoryId} />
          )}
        </div>
      </div>
      {plpATF && !isMobile && <LongDescription description={plpATF} isShortDescription={true} />}
    </>
  );
};

PlpTitleRowDesktop.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  sortData: PropTypes.array.isRequired,
  totalProducts: PropTypes.number.isRequired,
  bestRating: PropTypes.number,
  productCategoryName: PropTypes.string.isRequired,
  productList: PropTypes.number.isRequired,
  reviewCount: PropTypes.number.isRequired,
  onScrollToReviews: PropTypes.func.isRequired,
  categoryId: PropTypes.string.isRequired,
  isLoaded: PropTypes.bool,
  plpATF: PropTypes.string,
};

export default PlpTitleRowDesktop;
